/* =====================================================================
   LUMIQO — lq-shop-module.js (sections/lq-shop-module.liquid)
   [data-lq-shop] root · radios [data-lq-shop-radio] (value = block id)
   panes [data-lq-shop-pane="<block id>"] (gallery, head, details, CTA, details link) toggle with the radio
   thumbs [data-lq-shop-thumb="n"] → slides [data-lq-shop-slide="n"] inside the same pane
   quantity [data-lq-shop-qty="-1|1"] + [data-lq-shop-qty-input] → every [data-lq-add-to-cart] in the
   section (data-quantity / data-items quantities) + price label [data-lq-shop-price] (single products).
   Add to cart itself is handled by lq-theme.js. Uses window.Lumiqo only.
   ===================================================================== */
const initialised = new WeakMap();

/** @param {HTMLElement} root */
function setup(root) {
  if (initialised.has(root)) return;
  const abort = new AbortController();
  const { signal } = abort;
  initialised.set(root, abort);

  const radios = /** @type {HTMLInputElement[]} */ (Array.from(root.querySelectorAll('[data-lq-shop-radio]')));
  const panes = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-lq-shop-pane]')));
  const qtyInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-lq-shop-qty-input]'));
  const msg = root.querySelector('[data-lq-atc-message]');

  const qty = () => {
    if (!qtyInput) return 1;
    const q = Math.min(9, Math.max(1, parseInt(qtyInput.value, 10) || 1));
    qtyInput.value = String(q);
    return q;
  };

  const updateQuantity = () => {
    const q = qty();
    for (const btn of root.querySelectorAll('[data-lq-add-to-cart]')) {
      if (!(btn instanceof HTMLElement)) continue;
      if (btn.dataset.items) {
        try {
          const items = JSON.parse(btn.dataset.items).map((it) => ({ ...it, quantity: q }));
          btn.dataset.items = JSON.stringify(items);
        } catch {
          /* keep as rendered */
        }
        // bundle price text is per bundle: hide it when buying more than one
        const wrap = btn.querySelector('[data-lq-shop-price-wrap]');
        if (wrap instanceof HTMLElement) wrap.hidden = q > 1;
      } else {
        btn.dataset.quantity = String(q);
        const cents = Number(btn.dataset.lqShopPriceCents);
        const price = btn.querySelector('[data-lq-shop-price]');
        if (price && cents && window.Lumiqo?.money) price.textContent = window.Lumiqo.money(cents * q);
      }
      // keep the no-JS form fields in sync too
      const form = btn.closest('form');
      if (form) for (const field of form.querySelectorAll('input[name$="[quantity]"]')) if (field instanceof HTMLInputElement) field.value = String(q);
    }
  };

  /** @param {string} id */
  const show = (id) => {
    for (const pane of panes) pane.hidden = pane.dataset.lqShopPane !== id;
    if (msg) msg.textContent = '';
  };

  for (const radio of radios) {
    radio.addEventListener('change', () => { if (radio.checked) show(radio.value); }, { signal });
  }

  root.addEventListener(
    'click',
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const thumb = target?.closest('[data-lq-shop-thumb]');
      if (thumb instanceof HTMLElement) {
        const pane = thumb.closest('[data-lq-shop-pane]');
        if (!pane) return;
        const n = thumb.dataset.lqShopThumb;
        for (const t of pane.querySelectorAll('[data-lq-shop-thumb]')) t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
        for (const slide of pane.querySelectorAll('[data-lq-shop-slide]')) {
          if (slide instanceof HTMLElement) slide.hidden = slide.dataset.lqShopSlide !== n;
        }
        return;
      }
      const step = target?.closest('[data-lq-shop-qty]');
      if (step instanceof HTMLElement && qtyInput) {
        qtyInput.value = String(qty() + parseInt(step.dataset.lqShopQty || '0', 10));
        updateQuantity();
      }
    },
    { signal }
  );

  if (qtyInput) {
    qtyInput.addEventListener('change', updateQuantity, { signal });
    qtyInput.addEventListener('input', () => { if (qtyInput.value !== '') updateQuantity(); }, { signal });
  }

  // theme editor: selecting an option block selects it
  document.addEventListener(
    'shopify:block:select',
    (e) => {
      const block = e.target instanceof HTMLElement ? e.target : null;
      if (!block || !root.contains(block)) return;
      const radio = block.querySelector('[data-lq-shop-radio]');
      if (radio instanceof HTMLInputElement) {
        radio.checked = true;
        show(radio.value);
      }
    },
    { signal }
  );

  const checked = radios.find((r) => r.checked) || radios[0];
  if (checked) {
    checked.checked = true;
    show(checked.value);
  }
  updateQuantity();
}

/** @param {ParentNode} scope */
function init(scope = document) {
  for (const root of scope.querySelectorAll('[data-lq-shop]')) if (root instanceof HTMLElement) setup(root);
}

/** @param {ParentNode} scope */
function destroy(scope) {
  for (const root of scope.querySelectorAll('[data-lq-shop]')) {
    if (!(root instanceof HTMLElement)) continue;
    initialised.get(root)?.abort();
    initialised.delete(root);
  }
}

if (!window.__lqShopModule) {
  window.__lqShopModule = true;
  document.addEventListener('shopify:section:load', (e) => { if (e.target instanceof HTMLElement) init(e.target); });
  document.addEventListener('shopify:section:unload', (e) => { if (e.target instanceof HTMLElement) destroy(e.target); });
  init();
}
